import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, DollarSign, FileText, TrendingUp, Package, BarChart, MessageSquare, Video, HardDrive, FolderOpen, UserCheck, Sparkles, Activity, BarChart3, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    // Check if user is logged in
    if (!user) {
      navigate('/');
      return;
    }

    // Check if user is admin
    if (user.role !== 'admin') {
      alert('Access denied. Admin privileges required.');
      navigate('/');
      return;
    }

    fetchDashboardData();
  }, [user, authLoading, navigate]);

  const fetchDashboardData = async () => {
    try {
      // Fetch general dashboard stats
      const statsResponse = await fetch(`${BACKEND_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        
        // Fetch AI Credits overview
        try {
          const creditsResponse = await fetch(`${BACKEND_URL}/api/ai-credits/admin/overview`, {
            headers: {
              'Authorization': `Bearer ${user.email}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (creditsResponse.ok) {
            const creditsData = await creditsResponse.json();
            statsData.ai_credits = {
              total_used: creditsData.total_credits_used,
              total_allocated: creditsData.total_credits_allocated,
              utilization_rate: creditsData.utilization_rate,
              low_credits_count: creditsData.low_credits_users?.length || 0
            };
          }
        } catch (err) {
          console.error('Error fetching AI credits data:', err);
        }
        
        setStats(statsData);
      }

      // Fetch detailed user statistics
      const userStatsResponse = await fetch(`${BACKEND_URL}/api/admin/users-stats`, {
        headers: {
          'Authorization': `Bearer ${user.email}`,
          'Content-Type': 'application/json'
        }
      });

      if (userStatsResponse.ok) {
        const userStatsData = await userStatsResponse.json();
        setUserStats(userStatsData.users || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // If not admin, don't render anything (will redirect)
  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="mt-1 text-sm text-gray-600">Welcome back, {user?.name}</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/')} variant="outline">
                Back to Site
              </Button>
              <Button onClick={handleLogout} variant="destructive">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-8 border-b">
          <button
            onClick={() => navigate('/admin')}
            className="px-4 py-2 border-b-2 border-cyan-600 text-cyan-600 font-semibold"
          >
            Dashboard
          </button>
          <button
            onClick={() => navigate('/admin/users')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Users
          </button>
          <button
            onClick={() => navigate('/admin/researchers')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Researchers
          </button>
          <button
            onClick={() => navigate('/admin/subscriptions')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Subscriptions
          </button>
          <button
            onClick={() => navigate('/admin/reports')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Reports
          </button>
          <button
            onClick={() => navigate('/admin/support')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Support
          </button>
          <button
            onClick={() => navigate('/admin/agreements')}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Agreements
          </button>
        </div>

        {/* General Stats Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
              <Users className="h-5 w-5 text-cyan-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.users?.total || 0}</div>
              <p className="text-sm text-gray-500 mt-1">
                {stats?.users?.active || 0} active
              </p>
            </CardContent>
          </Card>

          {/* Active Subscriptions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Active Subscriptions</CardTitle>
              <Package className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.subscriptions?.active || 0}</div>
              <p className="text-sm text-gray-500 mt-1">
                of {stats?.subscriptions?.total || 0} total
              </p>
            </CardContent>
          </Card>

          {/* Projects */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Projects</CardTitle>
              <FileText className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.content?.projects || 0}</div>
              <p className="text-sm text-gray-500 mt-1">
                {stats?.content?.documents || 0} documents
              </p>
            </CardContent>
          </Card>

          {/* Revenue */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Monthly Revenue</CardTitle>
              <DollarSign className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">${stats?.revenue?.total || 0}</div>
              <p className="text-sm text-gray-500 mt-1">
                USD per month
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Credits Statistics */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-600" />
            AI Credits Overview
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Credits Used</CardTitle>
                <Activity className="h-5 w-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">{stats?.ai_credits?.total_used || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  This month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Credits Allocated</CardTitle>
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">{stats?.ai_credits?.total_allocated || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  Total available
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Utilization Rate</CardTitle>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {stats?.ai_credits?.utilization_rate?.toFixed(1) || 0}%
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Average usage
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Low Credits Users</CardTitle>
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">
                  {stats?.ai_credits?.low_credits_count || 0}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Need attention
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Virtual Rooms Statistics */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Virtual Rooms Statistics</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Rooms */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Rooms</CardTitle>
                <Video className="h-5 w-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.virtual_rooms?.total || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  {stats?.virtual_rooms?.active || 0} active
                </p>
              </CardContent>
            </Card>

            {/* Rooms Today */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Created Today</CardTitle>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.virtual_rooms?.today || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  Today's rooms
                </p>
              </CardContent>
            </Card>

            {/* Rooms This Week */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Created This Week</CardTitle>
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.virtual_rooms?.this_week || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  Last 7 days
                </p>
              </CardContent>
            </Card>

            {/* Rooms This Month */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Created This Month</CardTitle>
                <TrendingUp className="h-5 w-5 text-cyan-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.virtual_rooms?.this_month || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  This month
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Rooms by Plan */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Rooms by Subscription Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats?.virtual_rooms?.by_plan?.basic || 0}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Basic Plan</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {stats?.virtual_rooms?.by_plan?.premium || 0}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Premium Plan</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats?.virtual_rooms?.by_plan?.advanced || 0}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Advanced Plan</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Research Assistants Statistics */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Research Assistants</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Total Research Assistants */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Assistants</CardTitle>
                <UserCheck className="h-5 w-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.research_assistants?.total || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  Registered research assistants
                </p>
              </CardContent>
            </Card>

            {/* Researchers */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Researchers</CardTitle>
                <Users className="h-5 w-5 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.research_assistants?.researchers || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  Principal researchers
                </p>
              </CardContent>
            </Card>

            {/* Active Collaborations */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Active Collaborators</CardTitle>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.research_assistants?.active || 0}</div>
                <p className="text-sm text-gray-500 mt-1">
                  In active projects
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* User Statistics Table */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">User Statistics & Storage</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Virtual Rooms
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Projects
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Library Docs
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Project Files
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Storage Used
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userStats.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                          No user data available
                        </td>
                      </tr>
                    ) : (
                      userStats.map((userStat, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-gray-900">
                                {userStat.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {userStat.email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              userStat.subscription_plan === 'advanced' ? 'bg-purple-100 text-purple-800' :
                              userStat.subscription_plan === 'premium' ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {userStat.subscription_plan}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {userStat.virtual_rooms_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {userStat.projects_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {userStat.library_documents_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {userStat.project_files_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-gray-900">
                                {userStat.storage_used_display} / {userStat.storage_limit_display}
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div
                                  className={`h-2 rounded-full ${
                                    userStat.storage_percentage > 90 ? 'bg-red-600' :
                                    userStat.storage_percentage > 70 ? 'bg-yellow-600' :
                                    'bg-green-600'
                                  }`}
                                  style={{ width: `${Math.min(userStat.storage_percentage, 100)}%` }}
                                ></div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {userStat.storage_percentage}%
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              userStat.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {userStat.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => navigate('/admin/users')}
                className="w-full justify-start"
                variant="outline"
              >
                <Users className="mr-2 h-5 w-5" />
                Manage Users
              </Button>
              <Button
                onClick={() => navigate('/admin/researchers')}
                className="w-full justify-start"
                variant="outline"
              >
                <Users className="mr-2 h-5 w-5" />
                Manage Researchers
              </Button>
              <Button
                onClick={() => navigate('/admin/subscriptions')}
                className="w-full justify-start"
                variant="outline"
              >
                <Package className="mr-2 h-5 w-5" />
                Manage Subscriptions
              </Button>
              <Button
                onClick={() => navigate('/admin/reports')}
                className="w-full justify-start"
                variant="outline"
              >
                <BarChart className="mr-2 h-5 w-5" />
                View Reports
              </Button>
            </CardContent>
          </Card>

          {/* Platform Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Platform Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Researchers</span>
                <span className="font-semibold">{stats?.content?.researchers || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Documents</span>
                <span className="font-semibold">{stats?.content?.documents || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Projects</span>
                <span className="font-semibold">{stats?.content?.projects || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Admin Users</span>
                <span className="font-semibold">{stats?.users?.admins || 0}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
